/**
 * Policy Repository
 *
 * FERP CRITICAL: Repository pattern for PolicyRule CRUD operations
 *
 * FERP Principles:
 * - Repository pattern (NO direct GraphQL calls outside this file)
 * - Backend ID generation (useGenerateId for policy IDs)
 * - Data-driven (all policy rules from backend DynamoDB)
 * - Life-critical (complete error handling with user notifications)
 * - Domain-agnostic (works for all policy types)
 *
 * GraphQL Operations:
 * - createPolicyRule: Create new policy with backend Snowflake ID
 * - updatePolicyRule: Update existing policy (immutable audit)
 * - deletePolicyRule: Soft delete (preserves audit trail)
 * - listPolicyRules: Query by tenant (optional type filter)
 * - getPolicyRule: Single policy retrieval
 * - dryRunPolicy: Test conditions without executing actions
 */

import { ApolloClient, gql } from '@apollo/client';
import type {
  PolicyRule,
  PolicyCondition,
  PolicyAction,
  DryRunResult,
} from '../governance/hooks/useGovernance';

// ============================================================================
// GraphQL Fragments
// ============================================================================

const POLICY_CONDITION_FRAGMENT = gql`
  fragment PolicyConditionFields on PolicyCondition {
    field
    operator
    value
  }
`;

const POLICY_ACTION_FRAGMENT = gql`
  fragment PolicyActionFields on PolicyAction {
    type
    params
  }
`;

const POLICY_RULE_FRAGMENT = gql`
  fragment PolicyRuleFields on PolicyRule {
    policyId
    tenantId
    policyType
    policyName
    conditions {
      ...PolicyConditionFields
    }
    actions {
      ...PolicyActionFields
    }
    enabled
    priority
    createdAt
    createdBy
    updatedAt
    updatedBy
  }
  ${POLICY_CONDITION_FRAGMENT}
  ${POLICY_ACTION_FRAGMENT}
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

const CREATE_POLICY_RULE = gql`
  mutation CreatePolicyRule($input: CreatePolicyRuleInput!) {
    createPolicyRule(input: $input) {
      ...PolicyRuleFields
    }
  }
  ${POLICY_RULE_FRAGMENT}
`;

const UPDATE_POLICY_RULE = gql`
  mutation UpdatePolicyRule($input: UpdatePolicyRuleInput!) {
    updatePolicyRule(input: $input) {
      ...PolicyRuleFields
    }
  }
  ${POLICY_RULE_FRAGMENT}
`;

const DELETE_POLICY_RULE = gql`
  mutation DeletePolicyRule($input: DeletePolicyRuleInput!) {
    deletePolicyRule(input: $input)
  }
`;

const DRY_RUN_POLICY = gql`
  mutation DryRunPolicy($input: DryRunPolicyInput!) {
    dryRunPolicy(input: $input) {
      policyId
      matched
      conditionsEvaluated {
        condition {
          ...PolicyConditionFields
        }
        matched
        reason
      }
      actionsToExecute {
        ...PolicyActionFields
      }
      zenDsl
    }
  }
  ${POLICY_CONDITION_FRAGMENT}
  ${POLICY_ACTION_FRAGMENT}
`;

// ============================================================================
// GraphQL Queries
// ============================================================================

const LIST_POLICY_RULES = gql`
  query ListPolicyRules($tenantId: String!, $policyType: String) {
    listPolicyRules(tenantId: $tenantId, policyType: $policyType) {
      ...PolicyRuleFields
    }
  }
  ${POLICY_RULE_FRAGMENT}
`;

const GET_POLICY_RULE = gql`
  query GetPolicyRule($tenantId: String!, $policyId: String!) {
    getPolicyRule(tenantId: $tenantId, policyId: $policyId) {
      ...PolicyRuleFields
    }
  }
  ${POLICY_RULE_FRAGMENT}
`;

// ============================================================================
// Input Types (TypeScript)
// ============================================================================

export interface CreatePolicyRuleInput {
  tenantId: string;
  policyType: string;
  policyName: string;
  conditions: PolicyCondition[];
  actions: PolicyAction[];
  priority: number;
  enabled: boolean;
}

export interface UpdatePolicyRuleInput {
  policyId: string;
  tenantId: string;
  policyName?: string;
  conditions?: PolicyCondition[];
  actions?: PolicyAction[];
  priority?: number;
  enabled?: boolean;
}

export interface DeletePolicyRuleInput {
  policyId: string;
  tenantId: string;
}

export interface DryRunPolicyInput {
  policyId: string;
  tenantId: string;
  testData: Record<string, any>;
}

// ============================================================================
// Policy Repository Class
// ============================================================================

export class PolicyRepository {
  constructor(private apolloClient: ApolloClient) {}

  /**
   * Create policy rule
   *
   * FERP: Backend generates Snowflake ID (5-layer enforcement prevents client-side)
   */
  async create(input: CreatePolicyRuleInput): Promise<PolicyRule> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: CREATE_POLICY_RULE,
      variables: { input },
    });

    if (!data?.createPolicyRule) {
      throw new Error('Failed to create policy rule');
    }

    return this.mapToFrontendPolicy(data?.createPolicyRule);
  }

  /**
   * Update policy rule
   *
   * FERP: Immutable audit trail (backend tracks updated_at/by)
   */
  async update(input: UpdatePolicyRuleInput): Promise<PolicyRule> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: UPDATE_POLICY_RULE,
      variables: { input },
    });

    if (!data?.updatePolicyRule) {
      throw new Error('Failed to update policy rule');
    }

    return this.mapToFrontendPolicy(data?.updatePolicyRule);
  }

  /**
   * Delete policy rule (soft delete)
   *
   * FERP: Preserves audit trail, never truly deletes data
   */
  async delete(input: DeletePolicyRuleInput): Promise<boolean> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: DELETE_POLICY_RULE,
      variables: { input },
    });

    return data?.deletePolicyRule ?? false;
  }

  /**
   * List policy rules by tenant
   *
   * FERP: Optionally filter by policy type
   */
  async listByTenant(tenantId: string, policyType?: string): Promise<PolicyRule[]> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: LIST_POLICY_RULES,
      variables: { tenantId, policyType },
    });

    if (!data?.listPolicyRules) {
      return [];
    }

    return data?.listPolicyRules.map(this.mapToFrontendPolicy);
  }

  /**
   * Get single policy rule
   */
  async get(tenantId: string, policyId: string): Promise<PolicyRule | null> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: GET_POLICY_RULE,
      variables: { tenantId, policyId },
    });

    if (!data?.getPolicyRule) {
      return null;
    }

    return this.mapToFrontendPolicy(data?.getPolicyRule);
  }

  /**
   * Dry-run policy evaluation
   *
   * FERP: Tests Zen Engine conditions without executing actions
   */
  async dryRun(input: DryRunPolicyInput): Promise<DryRunResult> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: DRY_RUN_POLICY,
      variables: { input },
    });

    if (!data?.dryRunPolicy) {
      throw new Error('Failed to run policy dry-run');
    }

    return this.mapToDryRunResult(data.dryRunPolicy);
  }

  // ============================================================================
  // Private Mapping Functions (GraphQL → Frontend Types)
  // ============================================================================

  /**
   * Map GraphQL PolicyRule to frontend PolicyRule
   *
   * Converts camelCase GraphQL fields to snake_case frontend types
   */
  private mapToFrontendPolicy(gqlPolicy: any): PolicyRule {
    return {
      policy_id: gqlPolicy.policyId,
      tenant_id: gqlPolicy.tenantId,
      policy_type: gqlPolicy.policyType,
      policy_name: gqlPolicy.policyName,
      conditions: gqlPolicy.conditions.map(this.mapToFrontendCondition),
      actions: gqlPolicy.actions.map(this.mapToFrontendAction),
      enabled: gqlPolicy.enabled,
      priority: gqlPolicy.priority,
      created_at: gqlPolicy.createdAt,
      updated_at: gqlPolicy.updatedAt,
    };
  }

  /**
   * Map GraphQL PolicyCondition to frontend PolicyCondition
   */
  private mapToFrontendCondition(gqlCondition: any): PolicyCondition {
    return {
      field: gqlCondition.field,
      operator: this.mapOperatorFromGraphQL(gqlCondition.operator),
      value: gqlCondition.value,
    };
  }

  /**
   * Map GraphQL PolicyAction to frontend PolicyAction
   */
  private mapToFrontendAction(gqlAction: any): PolicyAction {
    return {
      type: this.mapActionTypeFromGraphQL(gqlAction.type),
      params: gqlAction.params,
    };
  }

  /**
   * Map GraphQL operator enum to frontend operator
   */
  private mapOperatorFromGraphQL(
    gqlOperator: string
  ): PolicyCondition['operator'] {
    const operatorMap: Record<string, PolicyCondition['operator']> = {
      EQUALS: 'equals',
      NOT_EQUALS: 'not_equals',
      GREATER_THAN: 'greater_than',
      LESS_THAN: 'less_than',
      CONTAINS: 'contains',
      REGEX: 'regex',
    };

    return operatorMap[gqlOperator] || 'equals';
  }

  /**
   * Map GraphQL action type enum to frontend action type
   */
  private mapActionTypeFromGraphQL(gqlActionType: string): PolicyAction['type'] {
    const actionTypeMap: Record<string, PolicyAction['type']> = {
      NOTIFY: 'notify',
      ESCALATE: 'escalate',
      BLOCK: 'block',
      APPROVE: 'approve',
      CUSTOM: 'custom',
    };

    return actionTypeMap[gqlActionType] || 'notify';
  }

  /**
   * Map GraphQL DryRunResult to frontend DryRunResult
   */
  private mapToDryRunResult(gqlResult: any): DryRunResult {
    return {
      policy_id: gqlResult.policyId,
      matched: gqlResult.matched,
      conditions_evaluated: gqlResult.conditionsEvaluated.map((evalResult: any) => ({
        condition: this.mapToFrontendCondition(evalResult.condition),
        matched: evalResult.matched,
        reason: evalResult.reason,
      })),
      actions_to_execute: gqlResult.actionsToExecute.map(this.mapToFrontendAction.bind(this)),
    };
  }
}

/**
 * Factory function to create PolicyRepository instance
 *
 * FERP: Use this in hooks/components to get repository instance
 */
export function createPolicyRepository(apolloClient: ApolloClient): PolicyRepository {
  return new PolicyRepository(apolloClient);
}
